import { isNegativeAmount } from '@financiar/shared';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Empty, List, Row, Statistic, Table, Tag, Typography, theme as antTheme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import type { DashboardRecentInvoice, DashboardUpcomingSubscription } from '../../api/types';
import { ActiveSubscriptionBadge } from '../../components/ActiveSubscriptionBadge';
import { DateText, MoneyText } from '../../lib/format';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { fetchDashboardExpenses, fetchDashboardSummary, fetchDashboardTrends } from './api';
import { ComparisonTrendCard, ExpenseCategoriesCard, MetricTrendCard, ProfitCard, VatThresholdCard } from './DashboardWidgets';

const { Title, Text } = Typography;

function RecentInvoiceStatusTag({ row }: { row: DashboardRecentInvoice }) {
  if (row.status === 'CANCELLED') return <Tag color="default">Anulată</Tag>;
  if (row.paymentStatus === 'PAID') return <Tag color="green">Încasată</Tag>;
  if (row.paymentStatus === 'PARTIAL') return <Tag color="orange">Parțial încasată</Tag>;
  return <Tag color="blue">Emisă</Tag>;
}

/**
 * `/` (task-8d-brief.md §3 / ruling R29) — the real dashboard, backed by the
 * single `GET /dashboard/summary?companyId=` call this task also adds on the
 * backend. `firmaId` is part of the query key, so switching the current firm
 * (FirmaCurenta) always refetches for the new company rather than showing
 * stale numbers.
 */
export function DashboardPage() {
  const { firma, firmaId, isLoading: isLoadingFirma } = useFirmaCurenta();
  const navigate = useNavigate();
  const {
    token: { colorPrimary, colorError },
  } = antTheme.useToken();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'summary', firmaId],
    queryFn: () => fetchDashboardSummary(firmaId as string),
    enabled: !!firmaId,
  });

  const { data: trends, isLoading: isLoadingTrends } = useQuery({
    queryKey: ['dashboard', 'trends', firmaId],
    queryFn: () => fetchDashboardTrends(firmaId as string),
    enabled: !!firmaId,
  });

  const { data: expenses, isLoading: isLoadingExpenses } = useQuery({
    queryKey: ['dashboard', 'expenses', firmaId],
    queryFn: () => fetchDashboardExpenses(firmaId as string),
    enabled: !!firmaId,
  });

  const recentColumns: ColumnsType<DashboardRecentInvoice> = [
    {
      title: 'Număr',
      key: 'fullNumber',
      render: (_, row) => row.fullNumber ?? <Tag>Ciornă</Tag>,
    },
    {
      title: 'Client',
      key: 'clientName',
      render: (_, row) => (
        <>
          {row.clientName}
          <ActiveSubscriptionBadge active={row.clientHasActiveSubscription} />
        </>
      ),
    },
    { title: 'Dată', key: 'issueDate', render: (_, row) => <DateText value={row.issueDate} /> },
    {
      title: 'Total',
      key: 'totalAmount',
      align: 'right',
      render: (_, row) => (
        <MoneyText value={row.totalAmount} className={isNegativeAmount(row.totalAmount) ? 'money-negative' : undefined} />
      ),
    },
    { title: 'Status', key: 'status', render: (_, row) => <RecentInvoiceStatusTag row={row} /> },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        Dashboard
      </Title>
      <Text type="secondary">{firma?.name ?? '—'}</Text>

      {!firmaId && !isLoadingFirma ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." style={{ marginTop: 48 }} />
      ) : (
        <>
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col xs={24} sm={8}>
              <Card loading={isLoading}>
                <Statistic
                  title="De încasat"
                  value={0}
                  valueStyle={{ color: colorPrimary }}
                  formatter={() => <MoneyText value={data?.deIncasat ?? '0.00'} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card loading={isLoading}>
                <Statistic title="Facturi luna aceasta" value={data?.facturiLunaAceasta.count ?? 0} />
                <Text type="secondary">
                  <MoneyText value={data?.facturiLunaAceasta.total ?? '0.00'} />
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card loading={isLoading}>
                <Statistic title="Restante" value={data?.restante.count ?? 0} valueStyle={{ color: colorError }} />
                <Text type="danger">
                  <MoneyText value={data?.restante.total ?? '0.00'} />
                </Text>
              </Card>
            </Col>
          </Row>

          {trends ? (
            <>
              <Row gutter={16} style={{ marginTop: 24 }}>
                <Col xs={24} md={12}>
                  <MetricTrendCard title="VÂNZĂRI" series={trends.sales} month={trends.month} countLabel="Facturi emise" loading={isLoadingTrends} />
                </Col>
                <Col xs={24} md={12}>
                  <MetricTrendCard title="ÎNCASĂRI" series={trends.collections} month={trends.month} countLabel="Încasări" loading={isLoadingTrends} />
                </Col>
              </Row>

              <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                  <VatThresholdCard vatThreshold={trends.vatThreshold} loading={isLoadingTrends} />
                </Col>
              </Row>

              {expenses ? (
                <Row gutter={16} style={{ marginTop: 24 }}>
                  <Col xs={24} md={12}>
                    <ExpenseCategoriesCard expenses={expenses} loading={isLoadingExpenses} />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProfitCard expenses={expenses} year={expenses.year} loading={isLoadingExpenses} />
                  </Col>
                </Row>
              ) : null}

              <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                  <ComparisonTrendCard title="GRAFIC VÂNZĂRI" series={trends.sales} year={trends.year} month={trends.month} loading={isLoadingTrends} />
                </Col>
              </Row>

              <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                  <ComparisonTrendCard title="GRAFIC ÎNCASĂRI" series={trends.collections} year={trends.year} month={trends.month} loading={isLoadingTrends} />
                </Col>
              </Row>
            </>
          ) : null}

          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col xs={24} md={12}>
              <Card title="Facturi recente" loading={isLoading}>
                {data && data.facturiRecente.length === 0 ? (
                  <Empty description="Nicio factură emisă încă." />
                ) : (
                  <Table<DashboardRecentInvoice>
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={data?.facturiRecente ?? []}
                    columns={recentColumns}
                    scroll={{ x: 'max-content' }}
                    onRow={(row) => ({
                      style: { cursor: 'pointer' },
                      onClick: () => navigate(`/facturi/${row.id}`),
                    })}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Abonamente ce urmează" loading={isLoading}>
                {data && data.abonamenteUrmatoare.length === 0 ? (
                  <Empty description="Niciun abonament activ." />
                ) : (
                  <List<DashboardUpcomingSubscription>
                    dataSource={data?.abonamenteUrmatoare ?? []}
                    footer={
                      data ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text strong>Total abonamente active ({data.abonamenteActive.count})</Text>
                          <Text strong>
                            <MoneyText value={data.abonamenteActive.total} />
                          </Text>
                        </div>
                      ) : null
                    }
                    renderItem={(item) => {
                      const daysUntil = dayjs(item.nextIssueDate).startOf('day').diff(dayjs().startOf('day'), 'day');
                      return (
                        <List.Item
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate('/abonamente')}
                          extra={<MoneyText value={item.valoare} />}
                        >
                          <List.Item.Meta
                            title={`${item.name} — ${item.clientName}`}
                            description={
                              <Tag color={daysUntil <= 3 ? 'red' : 'default'}>
                                <DateText value={item.nextIssueDate} />
                              </Tag>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
